import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { toast } from "react-toastify";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import {
  bulkPublishChangeSets,
  fetchReviewCounts,
  fetchReviewQueue,
  setReviewFilters,
} from "../../redux/slices/review";
import {
  clearReviewFiltersStorage,
  loadReviewFilters,
  reviewFiltersToSearchParams,
  saveReviewFilters,
} from "../../helpers/reviewFilters";

const TIERS = [
  {
    key: "ready",
    label: "Ready to publish",
    hint: "Auto-approved updates — safest bulk publish",
  },
  {
    key: "check",
    label: "Needs review",
    hint: "Critical or uncertain changes",
  },
  {
    key: "new",
    label: "New",
    hint: "First publish — bulk from this list",
  },
  {
    key: "needs_attention",
    label: "Needs attention",
    hint: "Coverage drop or crawl errors",
  },
];

const TIER_BADGE = {
  ready: "bg-emerald-50 text-emerald-800",
  check: "bg-amber-50 text-amber-900",
  new: "bg-blue-50 text-blue-800",
  needs_attention: "bg-red-50 text-red-800",
};

function fieldLabel(d) {
  if (d.label && d.label !== d.path) return d.label;
  if (d.path === "_entity") return null;
  if (!d.path) return null;
  return d.path
    .replace(/^structured\./, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function changeSummary(item) {
  const diff = item.diff || [];
  if (!diff.length) return "No field changes";

  const isNewEntity = diff.some(
    (d) =>
      d.path === "_entity" ||
      (d.changeType === "added" &&
        String(d.label || "")
          .toLowerCase()
          .includes("new "))
  );
  if (isNewEntity && item.tier === "new") {
    return `New ${item.entityType || "item"} — not published yet`;
  }

  const added = [];
  const updated = [];
  const removed = [];
  for (const d of diff) {
    const label = fieldLabel(d);
    if (!label) continue;
    if (d.changeType === "added") added.push(label);
    else if (d.changeType === "removed") removed.push(label);
    else updated.push(label);
  }

  const parts = [];
  const fmt = (list, verb) => {
    if (!list.length) return;
    const shown = list.slice(0, 3);
    const more = list.length - shown.length;
    parts.push(
      `${verb} ${shown.join(", ")}${more > 0 ? ` +${more} more` : ""}`
    );
  };
  fmt(added, "Added");
  fmt(updated, "Updated");
  fmt(removed, "Removed");

  if (!parts.length) {
    if (isNewEntity) return `New ${item.entityType || "item"}`;
    return `${diff.length} field change${diff.length === 1 ? "" : "s"}`;
  }
  return parts.join(" · ");
}

function tierLabel(tier) {
  return TIERS.find((t) => t.key === tier)?.label || tier || "—";
}

function liveDetailLink(item) {
  const type = item.entityType;
  const targetId = item.targetId;
  const schoolId = item.schoolId;

  if (type === "school") {
    if (targetId) {
      return { to: `/school/${targetId}`, label: "Open school" };
    }
    return { to: `/review/${item.id}`, label: "Review" };
  }

  if (type === "scholarship") {
    if (targetId) {
      return { to: `/scholarship/${targetId}`, label: "Open scholarship" };
    }
    return { to: `/review/${item.id}`, label: "Review" };
  }

  if (type === "programme") {
    if (targetId && schoolId) {
      return {
        to: `/school/${schoolId}/programmes?programme=${encodeURIComponent(
          targetId
        )}&isDraft=false`,
        label: "Open programme",
      };
    }
    return { to: `/review/${item.id}`, label: "Review" };
  }

  return { to: `/review/${item.id}`, label: "Review" };
}

function emptyHint(entityType, tier, counts = {}, coverageActive = false) {
  if (entityType === "school") {
    return "No pending school changes. Unchanged school crawls are auto-verified and never enter this queue — only field diffs show up here.";
  }
  if (entityType === "scholarship") {
    return "No pending scholarships in the queue. Re-crawl a school whose YAML has a scholarships section (e.g. Accademia Italiana) so ingest can create change sets.";
  }
  if (coverageActive) {
    return "No pending items match this coverage range. Widen min/max or clear filters.";
  }
  if (tier === "ready") {
    const newCount = counts.new ?? 0;
    if (newCount > 0) {
      return `No auto-approved updates yet. Ready is for safe edits to already-live items. You have ${newCount} New item(s) — open the New tab to bulk-publish first-time crawls.`;
    }
    return "Nothing ready for bulk publish yet. Ready = safe non-critical updates to already-published items.";
  }
  if (tier === "new") {
    return "No new (never-published) items pending.";
  }
  return `No pending change sets${tier ? ` in ${tier}` : ""}.`;
}

function parseCoverageParam(raw, fallback) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.round(n)));
}

const ReviewInbox = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    items,
    lastDocId,
    hasMore,
    counts,
    pageLoading,
    bulkLoading,
    error,
  } = useSelector((state) => state.review);

  const didHydrateFilters = useRef(false);

  // Restore last filters when landing on bare /review (e.g. SideNav).
  useEffect(() => {
    if (didHydrateFilters.current) return;
    didHydrateFilters.current = true;
    const hasUrlFilters =
      searchParams.has("tier") ||
      searchParams.has("type") ||
      searchParams.has("school") ||
      searchParams.has("covMin") ||
      searchParams.has("covMax") ||
      searchParams.has("body");
    if (hasUrlFilters) return;
    const stored = loadReviewFilters();
    if (!stored) return;
    const params = reviewFiltersToSearchParams(stored);
    if ([...params.keys()].length) {
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once on mount
  }, []);

  // URL is the source of truth — do not fall back to stale Redux filters
  // (that bug made "All" keep the previous type).
  const tier = searchParams.get("tier") || "";
  const entityType = searchParams.get("type") || "";
  const schoolId = searchParams.get("school") || "";
  const urlBodyMode = searchParams.get("body") || "";
  const urlCovMin = searchParams.has("covMin")
    ? parseCoverageParam(searchParams.get("covMin"), 0)
    : 0;
  const urlCovMax = searchParams.has("covMax")
    ? parseCoverageParam(searchParams.get("covMax"), 100)
    : 100;
  const coverageActive = urlCovMin > 0 || urlCovMax < 100;
  const coverageMin = coverageActive ? urlCovMin : null;
  const coverageMax = coverageActive ? urlCovMax : null;

  const [minText, setMinText] = useState(String(urlCovMin));
  const [maxText, setMaxText] = useState(String(urlCovMax));

  useEffect(() => {
    setMinText(String(urlCovMin));
    setMaxText(String(urlCovMax));
  }, [urlCovMin, urlCovMax]);

  const [selected, setSelected] = useState(() => new Set());
  // Which bulk button is in flight — shared redux flag alone put the spinner
  // on "Publish all matching" even when publishing selected.
  const [bulkTarget, setBulkTarget] = useState(null); // "all" | "selected" | null
  // llm = template + AI cleanup, template = structured HTML only, keep = leave body
  const [bodyMode, setBodyMode] = useState(() => {
    if (
      urlBodyMode === "llm" ||
      urlBodyMode === "template" ||
      urlBodyMode === "keep"
    ) {
      return urlBodyMode;
    }
    const stored = loadReviewFilters();
    if (
      stored?.bodyMode === "llm" ||
      stored?.bodyMode === "template" ||
      stored?.bodyMode === "keep"
    ) {
      return stored.bodyMode;
    }
    return "template";
  });

  useEffect(() => {
    if (urlBodyMode === "template" || urlBodyMode === "keep" || urlBodyMode === "llm") {
      setBodyMode(urlBodyMode);
    }
  }, [urlBodyMode]);

  // Persist filters so SideNav / back-to-inbox can restore them.
  useEffect(() => {
    saveReviewFilters({
      tier: tier || "",
      type: entityType || "",
      school: schoolId || "",
      covMin: coverageActive ? urlCovMin : null,
      covMax: coverageActive ? urlCovMax : null,
      bodyMode,
    });
  }, [tier, entityType, schoolId, coverageActive, urlCovMin, urlCovMax, bodyMode]);

  const byType = counts.byEntityType || {};
  const bySchool = counts.bySchool || [];

  useEffect(() => {
    dispatch(fetchReviewCounts({ status: "pending" }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      setReviewFilters({
        tier,
        entityType,
        schoolId,
        coverageMin,
        coverageMax,
      })
    );
    dispatch(
      fetchReviewQueue({
        status: "pending",
        tier,
        entityType,
        schoolId,
        coverageMin,
        coverageMax,
      })
    );
    setSelected(new Set());
  }, [dispatch, tier, entityType, schoolId, coverageMin, coverageMax]);

  const setTier = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("tier", next);
    else params.delete("tier");
    setSearchParams(params);
  };

  const setType = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("type", next);
    else params.delete("type");
    setSearchParams(params);
  };

  const setSchool = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("school", next);
    else params.delete("school");
    setSearchParams(params);
  };

  const commitCoverageRange = (min, max) => {
    const params = new URLSearchParams(searchParams);
    params.delete("coverage");
    if (min <= 0 && max >= 100) {
      params.delete("covMin");
      params.delete("covMax");
    } else {
      params.set("covMin", String(min));
      params.set("covMax", String(max));
    }
    setSearchParams(params);
  };

  const applyCoverageFields = () => {
    let lo = parseCoverageParam(minText, 0);
    let hi = parseCoverageParam(maxText, 100);
    if (lo > hi) {
      const swap = lo;
      lo = hi;
      hi = swap;
    }
    setMinText(String(lo));
    setMaxText(String(hi));
    commitCoverageRange(lo, hi);
  };

  const setBodyModeAndUrl = (next) => {
    setBodyMode(next);
    const params = new URLSearchParams(searchParams);
    if (next && next !== "template") params.set("body", next);
    else params.delete("body");
    setSearchParams(params);
  };

  const clearAllFilters = () => {
    clearReviewFiltersStorage();
    setBodyMode("template");
    setMinText("0");
    setMaxText("100");
    setSearchParams(new URLSearchParams());
  };

  const loadMore = () => {
    if (!lastDocId) return;
    dispatch(
      fetchReviewQueue({
        status: "pending",
        tier,
        entityType,
        schoolId,
        coverageMin,
        coverageMax,
        lastDocId,
      })
    );
  };

  const allVisibleIds = useMemo(() => items.map((i) => i.id), [items]);
  const allSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selected.has(id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allVisibleIds));
  };

  const runBulk = async ({ ids = null, bulkTier = null } = {}) => {
    const effectiveTier = bulkTier || tier;
    setBulkTarget(ids ? "selected" : "all");
    let publishedOk = false;
    try {
      const result = await dispatch(
        bulkPublishChangeSets({
          ids,
          tier: ids ? undefined : effectiveTier || "ready",
          entityType: ids ? undefined : entityType,
          schoolId: ids ? undefined : schoolId,
          coverageMin: ids ? undefined : coverageMin,
          coverageMax: ids ? undefined : coverageMax,
          convertBody: bodyMode !== "keep",
          polishBody: bodyMode === "llm",
          // Filtered mode walks the full matching queue (not just this page).
          limit: ids ? 50 : 200,
        })
      );
      if (bulkPublishChangeSets.fulfilled.match(result)) {
        publishedOk = true;
        const {
          published = [],
          failed = [],
          truncated = false,
        } = result.payload;
        if (published.length) {
          toast.success(`Published ${published.length} change set(s)`);
        }
        if (failed.length) {
          toast.error(
            `${failed.length} failed: ${failed
              .slice(0, 2)
              .map((f) => f.error)
              .join("; ")}`
          );
        }
        if (truncated) {
          toast.info(
            "Hit the 200-item safety cap — click Publish filtered again for the rest."
          );
        }
        if (!published.length && !failed.length) {
          toast.info("Nothing to publish");
        }
        dispatch(fetchReviewCounts({ status: "pending" }));
        dispatch(
          fetchReviewQueue({
            status: "pending",
            tier,
            entityType,
            schoolId,
            coverageMin,
            coverageMax,
          })
        );
      } else {
        toast.error(result.payload || "Bulk publish failed");
      }
    } finally {
      setBulkTarget(null);
      if (publishedOk) setSelected(new Set());
    }
  };

  const coverageLabel = coverageActive ? `${urlCovMin}%–${urlCovMax}%` : "";
  const bulkSafeTier = tier === "ready" || tier === "new";
  const selectedSchoolName =
    bySchool.find((s) => s.id === schoolId)?.name || null;
  const filterSummary = [
    tier ? tierLabel(tier) : null,
    entityType || null,
    selectedSchoolName || null,
    coverageLabel || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Review inbox
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Filter by university, tier, type, and coverage, then{" "}
              <span className="font-medium text-gray-700">
                Publish all matching
              </span>{" "}
              to ship every matching Ready/New item — including ones not loaded
              on this page (up to 200 per click).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Body
              <select
                className="rounded-lg border-gray-300 text-sm"
                value={bodyMode}
                onChange={(e) => setBodyModeAndUrl(e.target.value)}
                disabled={bulkLoading}
              >
                <option value="template">Template only</option>
                <option value="llm">LLM cleanup</option>
                <option value="keep">Keep existing body</option>
              </select>
            </label>
            <Button
              color="purple"
              disabled={bulkLoading || !bulkSafeTier || items.length === 0}
              onClick={() => runBulk()}
              title={
                bulkSafeTier
                  ? filterSummary
                    ? `Publishes all pending matches: ${filterSummary}`
                    : "Publishes all pending items in this tier"
                  : "Select Ready or New to bulk publish"
              }
            >
              {bulkTarget === "all" ? (
                <AiOutlineLoading className="animate-spin" />
              ) : (
                `Publish all matching${
                  filterSummary ? ` · ${filterSummary}` : ""
                }`
              )}
            </Button>
            {(selected.size > 0 || bulkTarget === "selected") && (
              <Button
                color="light"
                disabled={bulkLoading}
                onClick={() => runBulk({ ids: [...selected] })}
              >
                {bulkTarget === "selected" ? (
                  <AiOutlineLoading className="animate-spin" />
                ) : (
                  `Publish selected (${selected.size})`
                )}
              </Button>
            )}
          </div>
        </section>

        <section className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {TIERS.map((t) => {
            const active = tier === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTier(active ? "" : t.key)}
                className={`text-left rounded-lg border p-4 transition ${
                  active
                    ? "border-purple-700 bg-purple-50"
                    : "border-gray-200 bg-white hover:border-purple-300"
                }`}
              >
                <div className="text-2xl font-semibold text-purple-900">
                  {counts[t.key] ?? 0}
                </div>
                <div className="font-medium text-gray-800">{t.label}</div>
                <div className="text-xs text-gray-500 mt-1">{t.hint}</div>
              </button>
            );
          })}
        </section>

        <section className="mb-4 flex flex-wrap gap-4 items-center">
          <label className="text-sm text-gray-600">
            University
            <select
              className="ml-2 rounded-lg border-gray-300 text-sm max-w-xs"
              value={schoolId}
              onChange={(e) => setSchool(e.target.value)}
            >
              <option value="">All universities</option>
              {bySchool.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.count})
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-600">
            Type
            <select
              className="ml-2 rounded-lg border-gray-300 text-sm"
              value={entityType}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">
                All (
                {(byType.programme || 0) +
                  (byType.school || 0) +
                  (byType.scholarship || 0)}
                )
              </option>
              <option value="programme">
                Programme ({byType.programme ?? 0})
              </option>
              <option value="school">School ({byType.school ?? 0})</option>
              <option value="scholarship">
                Scholarship ({byType.scholarship ?? 0})
              </option>
            </select>
          </label>

          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span>Coverage</span>
            <label className="flex items-center gap-1">
              <span className="text-gray-500">Min</span>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                value={minText}
                aria-label="Minimum coverage percent"
                onChange={(e) => setMinText(e.target.value)}
                onBlur={applyCoverageFields}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                className="w-16 rounded-lg border-gray-300 text-sm tabular-nums"
              />
              <span className="text-gray-400">%</span>
            </label>
            <span className="text-gray-400">–</span>
            <label className="flex items-center gap-1">
              <span className="text-gray-500">Max</span>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                value={maxText}
                aria-label="Maximum coverage percent"
                onChange={(e) => setMaxText(e.target.value)}
                onBlur={applyCoverageFields}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                className="w-16 rounded-lg border-gray-300 text-sm tabular-nums"
              />
              <span className="text-gray-400">%</span>
            </label>
          </div>

          {(tier || entityType || schoolId || coverageActive) && (
            <button
              type="button"
              className="text-sm text-purple-800 hover:underline"
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
          )}
          {pageLoading && (
            <AiOutlineLoading className="animate-spin text-purple-700" />
          )}
        </section>

        {error && (
          <p className="text-sm text-red-600 mb-3">{String(error)}</p>
        )}

        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all visible"
                    className="rounded border-gray-300 text-purple-700"
                  />
                </th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 min-w-[14rem]">What changed</th>
                <th className="px-4 py-3">Coverage</th>
                <th className="px-4 py-3">Live page</th>
                <th className="px-4 py-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {!items.length && !pageLoading && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-500 max-w-xl mx-auto"
                  >
                    {emptyHint(entityType, tier, counts, coverageActive)}
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const live = liveDetailLink(item);
                return (
                  <tr
                    key={item.id}
                    className="border-t border-gray-100 hover:bg-purple-50/40"
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggleOne(item.id)}
                        aria-label={`Select ${item.title || item.id}`}
                        className="rounded border-gray-300 text-purple-700"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/review/${item.id}`}
                        className="font-medium text-purple-900 hover:underline"
                      >
                        {item.title || item.stableKey}
                      </Link>
                      {item.schoolId && item.entityType !== "school" && (
                        <div className="mt-0.5">
                          <Link
                            to={`/school/${item.schoolId}`}
                            className="text-xs text-gray-500 hover:text-teal-700 hover:underline"
                          >
                            {item.schoolName || "School"}
                          </Link>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">
                      {item.entityType}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          TIER_BADGE[item.tier] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {tierLabel(item.tier)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 leading-snug">
                      {changeSummary(item)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {Math.round((item.coverageBefore || 0) * 100)}% →{" "}
                      {Math.round((item.coverageAfter || 0) * 100)}%
                    </td>
                    <td className="px-4 py-3">
                      {live ? (
                        <Link
                          to={live.to}
                          className="text-teal-700 hover:underline whitespace-nowrap"
                        >
                          {live.label}
                        </Link>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.sourceUrl ? (
                        <a
                          href={item.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-teal-700 hover:underline"
                        >
                          Source site
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="mt-4">
            <Button color="purple" onClick={loadMore} disabled={pageLoading}>
              Load more
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReviewInbox;
